import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { functionBaseUrl, axiosWithToken } from "../../common/firebase";
import Swal from "sweetalert2";
import { isStudent } from "../../common/envConfig";

const Integration = ({ open, onClose, uid, subscriptionType }) => {
  const [urlObj, setUrlObj] = useState(null);
  const [params, setParams] = useState({});
  const [isTeeboxPartner, setIsTeeboxPartner] = useState(false);
  const [connectText, setConnectText] = useState(
    "Would you like to connect your Sportsbox 3DGolf account with Coach 360?"
  );

  useEffect(() => {
    const u = new URL(window.location.href);
    const p = Object.fromEntries(u.searchParams.entries());

    setUrlObj(u);
    setParams(p);

    const teebox =
      (p.partner && p.partner.toLowerCase() === "teebox") ||
      u.href.toLowerCase().includes("teebox");

    setIsTeeboxPartner(teebox);

    if (teebox) {
      setConnectText(
        "Would you like to connect your Sportsbox 3DGolf account with Teebox?"
      );
    } else {
      setConnectText(
        "Would you like to connect your Sportsbox 3DGolf account with Coach 360?"
      );
    }
  }, []);

  const redirectToTeebox = useCallback(async () => {
    const redirectUrlParam =
      params.redirect || params.redirectUrl || params.returnUrl;

    if (!redirectUrlParam) {
      Swal.fire({
        title: `<p style="font-size:70%;">Missing redirect URL for Teebox integration.</p>`,
        icon: "warning",
        showConfirmButton: false,
        showCloseButton: true,
        customClass: { container: "my-swal" },
      });
      onClose?.();
      return;
    }

    let redirect;
    try {
      redirect = new URL(redirectUrlParam);
    } catch {
      redirect = new URL(redirectUrlParam, window.location.origin);
    }

    const encryptedResp = await axiosWithToken(
      `${functionBaseUrl}/api/encrypt`,
      {
        method: "POST",
        data: { id: uid },
      }
    );

    // 🔥 Append Sportsbox user id (do NOT touch existing params)
    redirect.searchParams.set("sportsboxUserId", encryptedResp.data.id);

    window.location.href = redirect.toString();
    onClose?.();
  }, [params, uid, onClose]);

  const handleSubmit = useCallback(async () => {
    const u = urlObj || new URL(window.location.href);

    // ---- Teebox ----
    if (isTeeboxPartner) {
      redirectToTeebox();
      return;
    }

    // ---- Existing Golf Genius logic ----
    if (u.href.includes("partner") && u.href.includes("userId")) {
      if (params.userType === "student") {
        if (subscriptionType && !isStudent(subscriptionType)) {
          Swal.fire({
            title: `<p style="font-size:70%;">You are not subscribed to a student plan. Please update your plan to integrate with Golf Genius.</p>`,
            icon: "warning",
            showConfirmButton: false,
            showCloseButton: true,
            customClass: { container: "my-swal" },
          });
          onClose?.();
          return;
        }
      } else if (params.userType === "coach") {
        if (subscriptionType && isStudent(subscriptionType)) {
          Swal.fire({
            title: `<p style="font-size:70%;">You are not subscribed to a coach plan. Please upgrade your plan to integrate with Golf Genius.</p>`,
            icon: "warning",
            showConfirmButton: false,
            showCloseButton: true,
            customClass: { container: "my-swal" },
          });
          onClose?.();
          return;
        }
      }

      if (params.userId) {
        await axiosWithToken(functionBaseUrl + "/api/users/" + uid, {
          method: "PATCH",
          data: {
            userInfo: {
              golfGenius: { uid: params.userId, isActive: true },
              ggUserId: params.userId,
            },
          },
        }).catch((error) => {
          console.log(error);
        });
      }
    }

    const encryptedId = await axiosWithToken(`${functionBaseUrl}/api/encrypt`, {
      method: "POST",
      data: { id: uid },
    }).catch(() => {
      Swal.fire({
        title: `<p style="font-size:70%;">There was an error integrating your account, please refresh the page and try again.</p>`,
        icon: "success",
        showConfirmButton: false,
        allowOutsideClick: true,
        customClass: { container: "my-swal" },
      });
      onClose?.();
      return null;
    });

    await Swal.fire({
      title: `<p style="font-size:70%;">Successfully Integrated!</p>`,
      icon: "success",
      showConfirmButton: false,
      allowOutsideClick: true,
      timer: 2000,
      customClass: { container: "my-swal" },
    });

    if (isStudent(subscriptionType)) {
      const deepLink = `golfcoach://student_integrations?clientID=${encryptedId?.data?.id}&userType=student`;
      window.location.href = deepLink;
    } else {
      const deepLink = `golfcoach://coach_integrations?clientID=${encryptedId?.data?.id}&userType=coach`;
      window.location.href = deepLink;
    }

    onClose?.();
  }, [
    urlObj,
    params,
    isTeeboxPartner,
    redirectToTeebox,
    subscriptionType,
    uid,
    onClose,
  ]);

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        <h3 style={{ color: "#5C0DEE" }}>Integration</h3>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography>{connectText}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Yes
        </Button>
        <Button color="primary" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

Integration.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  uid: PropTypes.string,
  subscriptionType: PropTypes.string,
};

export default Integration;
